import { useNavigate } from 'react-router';
import {
  OutlineBestInDentalIcon,
  OutlineCreditCardIcon,
  OutlineDollarCircleIcon,
  OutlineDocumentTextIcon,
  OutlineUserIcon,
} from '@tempmee/react-icons';
import { useIsBestInDentalVisible, useIsOfficeBestInDentalVisible } from '~/domain/BestInDental';
import { useHasInvoices } from '~/domain/Invoices';
import { useOfficeWorkerClassification } from '~/domain/LocationSettings';
import { usePaymentExpiration } from '~/hooks/usePaymentExpiration';
import type { IUser } from '~/models/User';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { toViewedOfficePath } from '~/utils/viewedOfficeScope';
import type { SettingsMenuRow } from '@tempmee/react-blocks';
import { useScopedOfficeId } from './useScopedOfficeId';

/**
 * The Manage card of one location. Every row opens its page for this location
 * (`?officeId=`) and leaves the office selected in the header alone (GOTU-19472).
 * Best in Dental and Worker Type follow the viewed location, whichever office is
 * selected: the selected office keeps today's reads (the BID page's history
 * query, the redux state rule), and any other location is asked with its own
 * `x-office`.
 *
 * Visibility follows today's Account menu (`profile/Profile.js` `menuVisibility`)
 * for a page that always shows one office: Billing Details and Payment History
 * always; Invoices when the location has any (GOTU-15554); Worker Type when the
 * state allows a choice; Best in Dental when the program is visible. Worker Type
 * Management lives in the Organization section (`useOrganizationRows`).
 */
export const useManageRows = (office: IUser | undefined) => {
  const navigate = useNavigate();
  const isSelectedOfficeDualModel = useIsWorkerClassificationEnabled();
  const { visible: isSelectedOfficeBidVisible } = useIsBestInDentalVisible();
  const { isPaymentExpired, isPaymentExpiring } = usePaymentExpiration();
  const scopedOfficeId = useScopedOfficeId();
  const isScopedOffice = !!office && office.id === scopedOfficeId;
  const { data: viewedClassification } = useOfficeWorkerClassification(office?.id, {
    enabled: !!office && !isScopedOffice,
  });
  // Dual model, as `useIsWorkerClassificationEnabled` reads it: both are allowed.
  const isWorkerClassificationEnabled = isScopedOffice
    ? !!isSelectedOfficeDualModel
    : !!(viewedClassification?.contractor && viewedClassification?.employee);
  const isViewedOfficeBidVisible = useIsOfficeBestInDentalVisible(
    office?.id,
    !!office && !isScopedOffice,
  );
  const isBestInDentalVisible = isScopedOffice
    ? isSelectedOfficeBidVisible
    : isViewedOfficeBidVisible;
  const hasInvoices = useHasInvoices(office && { kind: 'location', officeId: office.id });

  if (!office) return [];

  const open = (route: string) =>
    navigate(toViewedOfficePath(route, { kind: 'location', officeId: office.id }));

  // Payment expiry is only known for the office the app is scoped to.
  let paymentAlert: SettingsMenuRow['alert'];
  if (isScopedOffice && isPaymentExpired) paymentAlert = 'error';
  else if (isScopedOffice && isPaymentExpiring) paymentAlert = 'warning';

  const rows: Array<SettingsMenuRow | false> = [
    {
      id: 'billing-details',
      label: 'Billing Details',
      icon: <OutlineCreditCardIcon />,
      alert: paymentAlert,
      onSelect: () => open(routes.paymentInformation),
      testID: 'payment-information',
    },
    {
      id: 'payment-history',
      label: 'Payment History',
      icon: <OutlineDollarCircleIcon />,
      onSelect: () => open(routes.paymentHistory),
      testID: 'payment-history',
    },
    // Any member location, whichever office is selected (UAT, GOTU-15553 comment 91237).
    isBestInDentalVisible && {
      id: 'best-in-dental',
      label: 'Best in Dental',
      icon: <OutlineBestInDentalIcon />,
      onSelect: () => open(routes.bestInDental),
      testID: 'best-in-dental',
    },
    // Any dual-model location, whichever office is selected (UAT, GOTU-15553 comment 91237).
    isWorkerClassificationEnabled && {
      id: 'worker-type',
      label: 'Worker Type',
      icon: <OutlineUserIcon />,
      alert: office.workerClassification?.classificationType ? undefined : 'error',
      onSelect: () => open(routes.workerClassificationType),
      testID: 'worker-type',
    },
    hasInvoices && {
      id: 'invoices',
      label: 'Invoices',
      icon: <OutlineDocumentTextIcon />,
      onSelect: () => open(routes.invoices),
      testID: 'invoices',
    },
  ];

  return rows.filter((row): row is SettingsMenuRow => !!row);
};
